import { definedMap } from "@glideapps/ts-necessities";

import {
    ComponentKind,
    ComponentDescription,
    PropertyDescription,
    TableGlideType,
    InputOutputTables,
    MutatingScreenKind,
    ClassScreenDescription,
    TypeSchema,
    findTable,
    getTableName,
} from "../description";
import { SerializedComponent } from "../../components/SerializedApp";
import { ComponentDescriptor, ComponentBuilder, ComponentConfiguratorContext, EditedColumns } from ".";
import { ComponentHandlerBase } from "./handler";
import { getColumnProperty } from "./property-utils";
import { isFavoritedColumnName } from "../../google-sheets";
import { makePrimaryKeyPropertyHandler, makeTextPropertyDescriptor } from "./descriptor-utils";
import { StringPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import { getDocURL } from "../../docUrl";
import { makeFavoriteAction, makeText } from "../builder-utils";

const ComponentKindFavorite: ComponentKind = "favorite";

const favoritePivotLabelPropertyHandler = new StringPropertyHandler(
    "pivotLabel",
    "Category",
    "",
    false,
    undefined,
    PropertySection.Design
);

interface FavoriteComponentDescription extends ComponentDescription {
    readonly primaryKeyProperty: PropertyDescription | undefined;

    readonly caption: PropertyDescription | undefined;
    readonly pivotLabel: PropertyDescription | undefined;
}

export class FavoriteComponentHandler extends ComponentHandlerBase<FavoriteComponentDescription> {
    constructor() {
        super(ComponentKindFavorite);
    }

    public getIsEditor(): boolean {
        return true;
    }

    public get isNonEmptyValidationStopper(): boolean {
        return true;
    }

    public getFavoritePivots(
        desc: FavoriteComponentDescription,
        screenDesc: ClassScreenDescription,
        schema: TypeSchema
    ): ReadonlyMap<TableGlideType, string | true> | undefined {
        const table = findTable(schema, screenDesc.type);
        if (table === undefined) return undefined;

        const pivotLabel = favoritePivotLabelPropertyHandler.getString(desc);
        return new Map([[table, pivotLabel ?? true]]);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: FavoriteComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): SerializedComponent {
        const { b } = builder;

        const favoriteAction = makeFavoriteAction(
            b,
            tables.output,
            mutatingScreenKind,
            definedMap(desc.primaryKeyProperty, getColumnProperty)
        );
        if (favoriteAction === undefined) return b.errorUI;

        const { lets, value, onChange, isEnabled } = favoriteAction;

        const caption = makeText(builder, desc.caption, tables.input, true) ?? b.localizedString("favorite");

        return b.let(
            lets,
            b.ui("ToggleSwitch", {
                value,
                onChange,
                isEnabled,
                caption,
                debounce: b.constant(false),
                visual: b.constant("favorite"),
            })
        );
    }

    public getDescriptor(
        _desc: FavoriteComponentDescription | undefined,
        table: TableGlideType | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<FavoriteComponentDescription> {
        const properties = [
            ...makePrimaryKeyPropertyHandler(ccc, table, true),
            makeTextPropertyDescriptor("caption", "Label", "", false, mutatingScreenKind, {
                propertySection: PropertySection.Design,
                searchable: false,
                emptyByDefault: true,
            }),
            favoritePivotLabelPropertyHandler,
            ...this.getBasePropertyDescriptors(),
        ];
        if (table?.rowIDColumn === undefined) {
            properties.unshift();
        }
        return {
            name: "Favorite",
            description: "Mark an item as a favorite",
            img: "componentFavorite",
            group: "Buttons",
            helpUrl: getDocURL("favorite"),
            needsInputContext: true,
            properties,
        };
    }

    public getEditedColumns(_desc: FavoriteComponentDescription, tables: InputOutputTables): EditedColumns {
        return [[isFavoritedColumnName, true, getTableName(tables.output)]];
    }
}
